import {
  faArrowLeftToLine,
  faArrowRightToLine,
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Flex, SxProps } from '@modusign/design-system';

import useResponsiveValue from '@app/hooks/useResponsiveValue';

import PaginationButton from './PaginationButton';

const bias = 0.01;
interface SwitchablePaginationProps extends SxProps {
  currentPage: number;
  maxPage: number;
  onClick: (nextPage: number) => void;
}

const defaultProps = {
  currentPage: 1,
  maxPage: 10,
  onClick() {},
};

function SwitchablePagination({ currentPage, maxPage, sx, onClick }: SwitchablePaginationProps) {
  const visibleRange = useResponsiveValue([5, 10]);

  function handleClick(number: number) {
    if (number !== currentPage) {
      onClick(number);
    }
  }

  const currentPaginationCycle = Math.floor(currentPage / (visibleRange + bias));

  const backRangePage = Math.max(1, currentPage - 1);
  const nextRangePage = Math.min(maxPage, currentPage + 1);

  const backPaginationDisabled = currentPage === 1;
  const nextPaginationDisabled = currentPage === maxPage;

  const pageNumbers: number[] = [];
  {
    const first = currentPaginationCycle * visibleRange + 1;
    const last = Math.min((currentPaginationCycle + 1) * visibleRange, maxPage);

    for (let i = first; i <= last; i += 1) {
      pageNumbers.push(i);
    }
  }

  return (
    <Box aria-label="page navigation" as="nav" mb="30px" sx={sx}>
      <Flex as="ul" justifyContent="center">
        <PaginationButton
          aria-label="Go to first page"
          disabled={backPaginationDisabled}
          onClick={() => handleClick(1)}
        >
          <Box as={FontAwesomeIcon} height="12px" icon={faArrowLeftToLine} width="11px" />
        </PaginationButton>
        <PaginationButton
          aria-label={`Go to ${backRangePage} page`}
          disabled={backPaginationDisabled}
          onClick={() => handleClick(backRangePage)}
        >
          <Box as={FontAwesomeIcon} height="12px" icon={faChevronLeft} width="8px" />
        </PaginationButton>
        {pageNumbers.map((i) => (
          <PaginationButton
            active={i === currentPage}
            aria-label={`Go to ${i} page`}
            key={i}
            onClick={() => handleClick(i)}
          >
            {i}
          </PaginationButton>
        ))}
        <PaginationButton
          aria-label={`Go to ${nextRangePage} page`}
          disabled={nextPaginationDisabled}
          onClick={() => handleClick(nextRangePage)}
        >
          <Box as={FontAwesomeIcon} height="12px" icon={faChevronRight} width="8px" />
        </PaginationButton>
        <PaginationButton
          aria-label="Go to last page"
          disabled={nextPaginationDisabled}
          onClick={() => handleClick(maxPage)}
        >
          <Box as={FontAwesomeIcon} height="12px" icon={faArrowRightToLine} width="11px" />
        </PaginationButton>
      </Flex>
    </Box>
  );
}

SwitchablePagination.defaultProps = defaultProps;

export default SwitchablePagination;
